import React, { useEffect, useMemo } from 'react';
import { ResponsiveGrid } from '@adobe/aem-react-editable-components';
import { ApolloProvider } from '@apollo/client';

import { getDeployedEnvType, useNextMiApolloClient } from '@marriott/mi-apollo-client-utils';
import { Layout, respGridUtil, prePageCall, getProcessEnvs } from '@marriott/mi-headless-utils';
import {
  constants,
  MIN_PAGE_HEIGHT,
  backgroundColorMapping,
  IMAGE_URL_DOMAIN,
  PAGES,
  otsUrlTypeValue,
  searchResultsPageUrl,
} from '@marriott/mi-rnb-components/constants';
import { getSubDirectoryLocale, PageParamsContext } from '@marriott/mi-rnb-components';
import operationSignatures from '../../_constants/operationSignatures.json';

const { NEXT_PUBLIC_AEM_PATH } = process.env;
const { ENV_DEV } = process.env;
const isDev = process.env['NODE_ENV'] === ENV_DEV;

const mockModel = async () => {
  if (isDev) {
    // nx-ignore-next-line
    const { searchResultsModel } = await import('@marriott/mi-rnb-components/localModel');
    return searchResultsModel;
  }
  return {};
};

const { ITEM_PATH_ROOT, ITEM_PATH_FOOTER, ITEM_PATH_HEADER } = constants;
const { SEARCH_RESULTS_PAGE } = PAGES;

// final data would have to come from 2 calls:
// 1. Page level call from AEM - that will populate the labels on components
// 2. Functional data for components like list of amenities and other related information
// import jsonContent from '../../mock/label-info.json';

export default function SearchResultsPage({ model, deployedEnvType, isAuthorMode, cookies, currentLocale }) {
  const MiApolloClient = useNextMiApolloClient(operationSignatures, deployedEnvType);

  const responsiveGridModel = respGridUtil(model?.cqItems?.root?.[':items']?.responsivegrid);
  const responsiveGridHeaderModel = respGridUtil(model?.cqItems?.root?.[':items']?.['header-responsivegrid']);
  const responsiveGridFooterModel = respGridUtil(model?.cqItems?.root?.[':items']?.['footer-responsivegrid']);
  const campaignParams = model?.cqItems?.root?.[':items']?.responsivegrid?.[':items'].campaignsparams;
  const rnberror = model?.cqItems?.root?.[':items']?.responsivegrid?.[':items'].rnberror;

  const contextValue = useMemo(
    () => ({ currentPage: SEARCH_RESULTS_PAGE, currentLocale, campaignParams, rnberror, userId: cookies.UserIdToken }),
    [currentLocale, campaignParams, rnberror, cookies.UserIdToken]
  );

  const {
    metaNames,
    metaProperties,
    canonical,
    title,
    staticComponentClientLibsJS,
    staticComponentClientLibsCSS,
    gridCss,
    hrefLangList,
    bgColor,
  } = model;

  //  Updating the meta properties and title as per SEO requirements
  const metaProperty = metaProperties;

  // Updating the background color for Home Page
  const bgColorSearch = backgroundColorMapping[bgColor];

  const updateMetaProperties = () => {
    const urlKey = metaNames?.find(item => item?.name === 'url');
    const titleKey = metaNames?.find(item => item?.name === 'title');

    metaProperty?.map(item => {
      if (item?.name === 'og:url' && !item?.value) item.value = urlKey?.value;
    });

    metaProperty?.map(item => {
      if (item?.name === 'og:title' && !item?.value) item.value = titleKey?.value;
    });

    metaProperty?.map(item => {
      if (item?.name === 'og:image') {
        if (!item.value.includes(IMAGE_URL_DOMAIN)) {
          const imgUrl = IMAGE_URL_DOMAIN + item.value;
          item.value = imgUrl;
        }
      }
    });
  };

  updateMetaProperties();

  //  Using this to Prevent The Click event default action
  useEffect(() => {
    //   const headerAccBtn = document.getElementsByClassName('m-header__acnt');
    const headerAccBtn = document.getElementsByClassName('m-header__acnt t-global-nav-items font-nav-items');
    if (!headerAccBtn) return;
    const headerAccBtnMain = Array?.prototype?.filter?.call(headerAccBtn, element => element?.nodeName === 'LI');
    if (!headerAccBtnMain) return;

    const handlePreventDefault = (event: MouseEvent) => {
      event.preventDefault();
    };

    headerAccBtnMain[0]?.addEventListener('click', handlePreventDefault);
    return () => headerAccBtnMain[0]?.removeEventListener('click', handlePreventDefault);
  }, []);

  return (
    <ApolloProvider client={MiApolloClient}>
      <PageParamsContext.Provider value={contextValue}>
        <Layout
          metaNames={metaNames}
          metaProps={metaProperties}
          title={title}
          staticComponentClientLibsCSS={staticComponentClientLibsCSS}
          staticComponentClientLibsJS={staticComponentClientLibsJS}
          isAuthorMode={isAuthorMode}
          gridCss={gridCss}
          canonical={canonical}
          hreflangList={hrefLangList}
        >
          {responsiveGridHeaderModel && (
            <ResponsiveGrid
              {...responsiveGridHeaderModel}
              model={responsiveGridHeaderModel}
              pagePath={NEXT_PUBLIC_AEM_PATH}
              itemPath={ITEM_PATH_HEADER}
            />
          )}
          <div style={{ background: bgColorSearch, minHeight: MIN_PAGE_HEIGHT }} className="search-page-container">
            <ResponsiveGrid
              {...responsiveGridModel}
              model={responsiveGridModel}
              pagePath={NEXT_PUBLIC_AEM_PATH}
              itemPath={ITEM_PATH_ROOT}
            />
          </div>
          {responsiveGridFooterModel && (
            <ResponsiveGrid
              {...responsiveGridFooterModel}
              model={responsiveGridFooterModel}
              pagePath={NEXT_PUBLIC_AEM_PATH}
              itemPath={ITEM_PATH_FOOTER}
            />
          )}
        </Layout>
      </PageParamsContext.Provider>
    </ApolloProvider>
  );
}

//Any product specific page data apart from common data like session, datalayer
//can be returned from this function and the same is passed as props to the page Component
const rnbSpecificData = async () => {
  //any page specific code can be written here.
  const deployedEnvType = getDeployedEnvType();
  const { GOOGLE_MAP_API_KEY, TARGET_SEGMENT_MBOX_NAME, TARGET_SEGMENT_PROPERTY_TOKEN } = getProcessEnvs();

  return {
    operationSignatures: operationSignatures,
    deployedEnvType,
    GOOGLE_MAP_API_KEY,
    TARGET_SEGMENT_MBOX_NAME,
    TARGET_SEGMENT_PROPERTY_TOKEN,
  };
};

//Make pre page calls like session, datalayer, fetch model.json, producSpecifc data etc
//at server side and pass the data is passed as props to page component.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getServerSideProps = async context => {
  const response = prePageCall(rnbSpecificData, mockModel, false, true, true);
  const componentProps = await response(context).then(data => data);
  const isOpenTextSearch = context?.query?.type && context?.query?.type === otsUrlTypeValue ? true : false;
  const currentLocale = context?.req?.['headers']?.['accept-language'] ?? 'en-US';
  const subLocale = getSubDirectoryLocale(currentLocale).replace('/', '');

  if (isOpenTextSearch && currentLocale === 'en-US') {
    return {
      redirect: {
        destination: `/${subLocale}${searchResultsPageUrl}`,
        permanent: false,
      },
    };
  } else {
    return {
      props: {
        deployedEnvType: componentProps?.props?.apolloEnvVars?.DEPLOYED_ENV_TYPE,
        ...componentProps.props,
      },
    };
  }
};
